import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { EntityType, Attribute, AttributeDataType, QueryFilter } from '../types';
import type { Entity } from '../types';
import type { ColumnDef } from '../config/viewsConfig';
import { loadModalAttributeContext, jsonKeysToColumnDefs } from '../utils/discoverAttributes';
import './Modal.css';
import './ColumnPicker.css';

interface ColumnPickerProps {
  entityType: EntityType;
  defaultColumns: ColumnDef[];
  /** Curated columns offered in the picker but not visible by default. */
  optionalColumns?: ColumnDef[];
  visibleColumns: ColumnDef[];
  onColumnsChange: (columns: ColumnDef[]) => void;
  entities?: Entity[];
  extraFilters?: QueryFilter[];
}

interface ColumnGroup {
  id: string;
  label: string;
  badge?: string;
  columns: ColumnDef[];
}

function attrToColumnDef(attr: Attribute): ColumnDef {
  const renderMap: Record<string, ColumnDef['render']> = { date: 'date', json: 'json' };
  return { key: attr.name, label: attr.name, render: renderMap[attr.data_type] };
}

function inferType(col: ColumnDef): AttributeDataType | undefined {
  if (col.key === 'id') return 'number';
  if (col.render === 'date') return 'date';
  if (col.key.includes('.')) return 'text';
  if (col.extract) return 'json';
  return undefined;
}

function columnMatchesSearch(col: ColumnDef, searchRaw: string): boolean {
  const search = searchRaw.trim().toLowerCase();
  if (!search) return true;
  if (col.key.toLowerCase().includes(search)) return true;
  if ((col.label || '').toLowerCase().includes(search)) return true;
  if (col.extract?.from && col.extract.path) {
    const composite = `${col.extract.from}.${col.extract.path}`.toLowerCase();
    if (composite.includes(search)) return true;
  }
  return false;
}

export const ColumnPicker: React.FC<ColumnPickerProps> = ({
  entityType,
  defaultColumns,
  optionalColumns = [],
  visibleColumns,
  onColumnsChange,
  entities: sampleEntities = [],
  extraFilters = [],
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ColumnDef[]>(visibleColumns);
  const [directColumns, setDirectColumns] = useState<ColumnDef[]>(defaultColumns);
  const [fkGroups, setFkGroups] = useState<ColumnGroup[]>([]);
  const [jsonGroups, setJsonGroups] = useState<ColumnGroup[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, AttributeDataType>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [attrSearch, setAttrSearch] = useState('');

  useEffect(() => {
    setSelected(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { directAttrs, fkGroups: fkGroupsResult, jsonAttrNames, discoveredJson } =
          await loadModalAttributeContext(entityType, {
            extraFilters,
            fallbackEntities: sampleEntities,
            cancelled: () => cancelled,
          });
        if (cancelled) return;

        const types: Record<string, AttributeDataType> = { id: 'number' };
        for (const a of directAttrs) types[a.name] = a.data_type;
        for (const c of defaultColumns) {
          if (!types[c.key]) {
            const t = inferType(c);
            if (t) types[c.key] = t;
          }
        }

        const knownKeys = new Set(defaultColumns.map(c => c.key));
        const attrColumns = directAttrs
          .filter((a: Attribute) => !knownKeys.has(a.name))
          .map(attrToColumnDef);

        const idCol: ColumnDef = { key: 'id', label: 'ID' };
        const merged = [
          ...(defaultColumns.some(c => c.key === 'id') ? [] : [idCol]),
          ...defaultColumns,
          ...attrColumns,
        ];
        setDirectColumns(merged);

        const fkColumnGroups: ColumnGroup[] = fkGroupsResult.flatMap(g => {
          const attrColumns: ColumnDef[] = g.attrs.map(ra => {
            const colKey = `${g.fkAttrName}.${ra.name}`;
            types[colKey] = ra.data_type;
            return {
              key: colKey,
              label: ra.name,
              render: (ra.data_type === 'date' ? 'date' : undefined) as ColumnDef['render'],
            };
          });
          const groups: ColumnGroup[] = [{ id: g.id, label: g.label, columns: attrColumns }];

          if (g.discoveredJson && g.discoveredJson.length > 0) {
            for (const { attrName, keys } of g.discoveredJson) {
              const jsonCols: ColumnDef[] = keys.map(k => {
                const colKey = `${g.fkAttrName}.${attrName}_${k.key}`;
                types[colKey] = 'json';
                return {
                  key: colKey,
                  label: `${attrName}.${k.path}`,
                  extract: { from: attrName, path: k.path },
                  sortable: false,
                };
              });
              if (jsonCols.length > 0) {
                groups.push({
                  id: `${g.id}_json_${attrName}`,
                  label: `${attrName} (via ${g.fkAttrName})`,
                  badge: 'json',
                  columns: jsonCols,
                });
              }
            }
          }
          return groups;
        });
        setFkGroups(fkColumnGroups);
        setTypeMap(types);

        if (!cancelled && jsonAttrNames.length > 0) {
          const jGroups = jsonKeysToColumnDefs(discoveredJson, entityType).map(g => ({
            ...g, badge: 'json' as string, columns: g.columns,
          }));
          setJsonGroups(jGroups);
          for (const g of jGroups) {
            for (const c of g.columns) types[c.key] = 'json';
          }
          setTypeMap({ ...types });
        }
      } catch (err) {
        console.warn('Failed to load attributes for column picker:', err);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, entityType, defaultColumns, sampleEntities, extraFilters]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const isSelected = useCallback(
    (key: string) => selected.some(c => c.key === key),
    [selected],
  );

  const addColumn = (col: ColumnDef) => {
    if (!isSelected(col.key)) setSelected(prev => [...prev, col]);
  };

  const removeColumn = (key: string) => {
    setSelected(prev => prev.filter(c => c.key !== key));
  };

  const moveColumn = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= selected.length) return;
    setSelected(prev => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx: number) => {
    if (dragIdx !== null && dragIdx !== idx) moveColumn(dragIdx, idx);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const handleApply = () => { onColumnsChange(selected); setOpen(false); };
  const handleReset = () => setSelected(defaultColumns);
  const handleOverlayClick = (e: React.MouseEvent) => { if (e.target === e.currentTarget) setOpen(false); };

  const toggleGroup = (groupId: string) => {
    setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const hasSearch = attrSearch.trim().length > 0;

  // Curated optional columns always carry a json badge even if discovery (which
  // populates typeMap) didn't sample them.
  const effectiveTypeMap = useMemo<Record<string, AttributeDataType>>(() => {
    const optionalTypes: Record<string, AttributeDataType> = {};
    for (const c of optionalColumns) optionalTypes[c.key] = 'json';
    return { ...optionalTypes, ...typeMap };
  }, [typeMap, optionalColumns]);

  const allGroups = useMemo<ColumnGroup[]>(() => {
    const groups: ColumnGroup[] = [
      { id: 'direct', label: `Direct attributes`, columns: directColumns },
      ...fkGroups.map(g => ({ ...g, columns: [...g.columns] })),
      ...jsonGroups.map(g => ({ ...g, columns: [...g.columns] })),
    ];

    // Merge curated optional columns into the discovered json group matching
    // extract.from (e.g. "worksheet_data (via test)"), creating it if discovery
    // found none. Only json-badged groups are considered so we don't land in the
    // "Direct attributes" group (whose element columns also extract from
    // worksheet_data).
    for (const opt of optionalColumns) {
      // Already offered under Direct attributes (e.g. HQ Mn, Cu1 Cu/Fe).
      // Skip so the picker does not list the same key twice with the same label.
      if (defaultColumns.some(c => c.key === opt.key)) continue;
      const from = opt.extract?.from;
      const path = opt.extract?.path;
      let target = groups.find(
        g => g.badge === 'json' && g.columns.some(c => c.extract?.from === from),
      );
      if (!target) {
        target = { id: `optional_json_${from}`, label: `${from} (via ${entityType})`, badge: 'json', columns: [] };
        groups.push(target);
      }
      const deduped = target.columns.filter(
        c => !(c.extract?.from === from && c.extract?.path === path),
      );
      target.columns = deduped.some(c => c.key === opt.key) ? deduped : [...deduped, opt];
    }

    return groups.map(g => ({
      ...g,
      columns: g.columns.filter(c => !isSelected(c.key) && columnMatchesSearch(c, attrSearch)),
    })).filter(g => g.columns.length > 0);
  }, [directColumns, fkGroups, jsonGroups, optionalColumns, defaultColumns, entityType, isSelected, attrSearch]);

  const totalAvailable = allGroups.reduce((sum, g) => sum + g.columns.length, 0);

  return (
    <div className="column-picker">
      <button
        className="column-picker-btn"
        onClick={() => { setAttrSearch(''); setOpen(true); }}
        title="Configure columns"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="1" width="14" height="14" rx="2" />
          <line x1="5.5" y1="1" x2="5.5" y2="15" />
          <line x1="10.5" y1="1" x2="10.5" y2="15" />
          <line x1="1" y1="5.5" x2="15" y2="5.5" />
          <line x1="1" y1="10.5" x2="15" y2="10.5" />
        </svg>
      </button>

      {open && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-container column-picker-modal" role="dialog" aria-modal="true" aria-label="Configure columns">
            <div className="modal-header">
              <h3>Configure Columns</h3>
              <button className="modal-close" onClick={() => setOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>

            <div className="cp-body">
              <div className="cp-left">
                <div className="fm-search-wrapper">
                  <svg className="fm-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="6.5" cy="6.5" r="5" />
                    <line x1="10" y1="10" x2="15" y2="15" />
                  </svg>
                  <input
                    type="text"
                    className="fm-search-input"
                    placeholder="Search attributes..."
                    value={attrSearch}
                    onChange={e => setAttrSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="cp-available-list">
                  <span className="column-picker-section-title">Available ({totalAvailable})</span>
                  {allGroups.map(group => (
                    <div key={group.id} className="modal-group">
                      <button
                        className="modal-group-header"
                        onClick={() => toggleGroup(group.id)}
                      >
                        <span className={`modal-group-arrow ${(hasSearch || expanded[group.id]) ? 'expanded' : ''}`}>&#9656;</span>
                        <span className="modal-group-label">{group.label}</span>
                        <span className="modal-group-count">{group.columns.length}</span>
                      </button>
                      {(hasSearch || expanded[group.id]) && (
                        <div className="column-picker-list">
                          {group.columns.map(col => (
                            <div
                              key={col.key}
                              className="column-picker-item available"
                              onClick={() => addColumn(col)}
                            >
                              <span className="column-picker-checkbox" />
                              <span className="column-picker-item-label">{col.label}</span>
                              {effectiveTypeMap[col.key] && (
                                <span className="data-type-badge" data-type={effectiveTypeMap[col.key]}>{effectiveTypeMap[col.key]}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {totalAvailable === 0 && (
                    <div className="column-picker-empty">
                      {hasSearch ? 'No matching attributes' : 'All columns visible'}
                    </div>
                  )}
                </div>
              </div>

              <div className="cp-right">
                <div className="column-picker-section-header">
                  <span className="column-picker-section-title">Selected ({selected.length})</span>
                  <button className="column-picker-reset" onClick={handleReset}>Reset</button>
                </div>
                <div className="cp-selected-list">
                  {selected.length === 0 && (
                    <div className="column-picker-empty">No columns selected</div>
                  )}
                  {selected.map((col, idx) => (
                    <div
                      key={col.key}
                      className={`column-picker-item selected ${dragIdx === idx ? 'dragging' : ''} ${dragOverIdx === idx ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={() => handleDrop(idx)}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="column-picker-drag-handle" title="Drag to reorder">⠿</span>
                      <span className="column-picker-item-label">{col.label}</span>
                      {effectiveTypeMap[col.key] && (
                        <span className="data-type-badge" data-type={effectiveTypeMap[col.key]}>{effectiveTypeMap[col.key]}</span>
                      )}
                      <button
                        className="column-picker-remove"
                        onClick={() => removeColumn(col.key)}
                        title="Remove column"
                        aria-label={`Remove ${col.label}`}
                      >&times;</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="column-picker-cancel" onClick={() => setOpen(false)}>Cancel</button>
              <button className="column-picker-apply" onClick={handleApply}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
